import { useState, Dispatch, SetStateAction, useEffect } from 'react';
import cc from 'classcat';
import { Checkbox } from '../Checkbox';

interface SelectItem {
    id: string;
    label: string;
    selected: boolean;
}

interface MultiSelectDropdownProps {
    label?: string;
    selectTextNoneSelected: string;
    selectTextSomeSelected: string;
    selectTextAllSelected: string;
    items: SelectItem[];
    setItems: Dispatch<SetStateAction<SelectItem[]>>;
    selectAllLabel: string;
    deselectAllLabel: string;
}

const arrowDownSrc = '/svg/arrow-down.svg';
const arrowUpSrc = '/svg/arrow-up.svg';

const determineSelectText = (items: SelectItem[], none: string, some: string, all: string): string => {
    let selectedCount = items.filter(item => item.selected).length;
    if (selectedCount === 0) return none;
    if (selectedCount === items.length) return all;
    return some;
};

const determineSelectAllToggleLabel = (items: SelectItem[], selectAllLabel: string, deselectAllLabel: string): string => {
    return items.every(item => item.selected) ? deselectAllLabel : selectAllLabel;
};

export const MultiSelectDropdown = ({
    label,
    selectTextNoneSelected: none,
    selectTextSomeSelected: some,
    selectTextAllSelected: all,
    items,
    setItems,
    selectAllLabel,
    deselectAllLabel,
}: MultiSelectDropdownProps) => {
    const [openMenu, setOpenMenu] = useState<boolean>(false);
    const [selectText, setSelectText] = useState<string>(determineSelectText(items, none, some, all));
    const [selectAllToggleLabel, setSelectAllToggleLabel] = useState<string>(determineSelectAllToggleLabel(items, selectAllLabel, deselectAllLabel));

    useEffect(() => {
        setSelectText(determineSelectText(items, none, some, all));
        setSelectAllToggleLabel(determineSelectAllToggleLabel(items, selectAllLabel, deselectAllLabel));
    }, [items, none, some, all, selectAllLabel, deselectAllLabel]);

    const selectAll = () => {
        const isSelectAll = selectAllToggleLabel === selectAllLabel;
        setItems(items.map(item => ({ ...item, selected: isSelectAll })));
    };

    const handleChange = (idx: number): void => {
        const newItems = [...items];
        newItems[idx].selected = !items[idx].selected;
        setItems(newItems);
    };

    useEffect(() => {
        setSelectText(determineSelectText(items, none, some, all));
    }, [items, none, some, all]);

    return (
        <div className={cc(['multi-select-dropdown', openMenu && 'multi-select-dropdown--bubbled'])}>
            {label && <p className="paragraph-4 multi-select-dropdown__select-label">{label}</p>}

            <button type="button" className="dropdown__select" onClick={() => setOpenMenu(prev => !prev)}>
                <div className="multi-select-dropdown__select">
                    <p className={cc(['paragraph-4', 'multi-select-dropdown__text', openMenu && 'multi-select-dropdown__text--active'])}>
                        {selectText}
                    </p>
                    <img src={openMenu ? arrowUpSrc : arrowDownSrc} alt="Toggle select" />
                </div>
            </button>

            {openMenu && (
                <>
                    <div className="dropdown__menu multi-select-dropdown__menu">
                        <p className="multi-select-dropdown__select-all-label paragraph-4" onClick={selectAll}>
                            {selectAllToggleLabel}
                        </p>
                        {items.map((item, idx) => (
                            <Checkbox id={item.id} key={item.id} label={item.label} checked={item.selected} handleChange={() => handleChange(idx)} />
                        ))}
                    </div>
                    <div className="multi-select-dropdown__event-bubbling-mask" onClick={() => setOpenMenu(false)}></div>
                </>
            )}
        </div>
    );
};
